import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import Column from "@amzn/meridian/esm/components/column";
import { AIMS_STATUS, TABS } from "../../constants";
import AimsMasthead from "../common/AimsMasthead";
import Summaries from "../inMeeting/summary/Summaries";
import SummarizeBox from "../organizer/SummarizeBox";
import Help from ".././inMeeting/Help";
import Loading from "../common/Loading";
import AttendeeHomeView from "../attendee/AttendeeHomeView";
import AimsNotAvailable from "../organizer/AimsNotAvailable";
import AimsException from "../common/AimsException";
import LogManager from "../utils/LogManager";
import { fetchAimsAvailabilityApi } from "../utils/AimsAvailabilityUtil";
import {AimsAvailabilityApiResponse} from "../../localtest/LocalhostTest";

/**
 * This is the main entry point for the AiMS Zoom App.
 *
 * - It initializes the Zoom SDK and, based on the running context (where the app is running)
 *   and user role (host or attendee), renders the appropriate view.
 * - If the user is the meeting organizer and is in a meeting, it calls the `/is-aims-available` API
 *   to determine the meeting type.
 *   The meeting type impacts:
 *     1) Whether the host can see the "Summarize" button (whether AiMS can be enabled)
 *     2) Who will receive the generated meeting summary
 */
const Home = ({ lastKnownContext, setLastKnownContext }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultTab = location.state?.selectedTab || "main";
  const [isLoading, setIsLoading] = useState(true);
  //isOrganizer means it's Outlook meeting organizer
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);

  //TODO: change AIMS status to null as default status once the bug fixed: https://sim.amazon.com/issues/MEETEX-169856
  const [aimsStatus, setAimsStatus] = useState(AIMS_STATUS.AVAILABLE);

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [runningContext, setRunningContext] = useState("inMeeting");
  const [userContext, setUserContext] = useState({});
  const [meetingUUID, setMeetingUUID] = useState(null);
  const [meetingId, setMeetingId] = useState(null);

  /**
   * Process AIMS availability response and update component state accordingly.
   * @param {object} aimsAvailability - Parsed JSON object from AIMS availability API.
   */
  const handleAimsAvailabilityResult = (aimsAvailability) => {
    if (!aimsAvailability) {
      LogManager.warn("The aimsAvailability value is null, show exception view.");
      setAimsStatus(AIMS_STATUS.EXCEPTION);
      return;
    }

    const {
      result: AimsAvailableResult,
      message: AimsAvailableMessage,
      hasPandC,
      warningFromZoom,
      warningFromOutlook,
      hasExternalUser,
      isOrganizer,
      alias,
    } = aimsAvailability;

    LogManager.info("The aimsAvailability result is:", aimsAvailability);

    if (typeof isOrganizer === "boolean") {
      setIsOrganizer(isOrganizer);
    }

    if (AimsAvailableResult === "AimsAvailable") {
      if (hasPandC) {
        LogManager.info("This is privileged_and_confidential meeting.");
        setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      } else {
        LogManager.info("This is low risk meeting.");
        setAimsStatus(AIMS_STATUS.AVAILABLE);
      }
    } else if (AimsAvailableResult === "AimsNotAvailable") {
      LogManager.info("This is AimsNotAvailable meeting.");
      setAimsStatus(AIMS_STATUS.NOT_AVAILABLE);
    } else {
      LogManager.warn("Unknown AimsAvailableResult value:", AimsAvailableResult);
      setAimsStatus(AIMS_STATUS.EXCEPTION);
    }
  };

  /**
   * Initialize Zoom SDK, fetch meeting and user context and check AIMS availability.
   */
  const initializeZoomSdk = async () => {
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Skipping zoomSdk.config() in localhost");
      return;
    }
    try {
      const configResponse = await zoomSdk.config({
        popoutSize: { width: 320, height: 760 },
        capabilities: [
          "getAppContext",
          "getMeetingContext",
          "getRunningContext",
          "getUserContext",
          "openUrl",
          "getMeetingUUID",
          "authorize",
          "onAuthorized",
          "sendMessageToChat",
        ],
      });
      LogManager.info("Zoom SDK Configured Successfully:", configResponse);

      const runningContext = await zoomSdk.getRunningContext();
      LogManager.info("The runningContext is:", runningContext);

      setRunningContext(runningContext.context);
      setLastKnownContext(runningContext.context);

      if (runningContext.context === "inMainClient") {
        //TODO: Logging - find a identifier in inMainClient for the user
        LogManager.info("The app is running in main client now, show MainClientHome view.");
        return;
      }

      const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
      console.log("The meetingUUID is:" + meetingUUID);
      setMeetingUUID(meetingUUID);

      const userContext = await zoomSdk.getUserContext();
      LogManager.info("The userContext is:", userContext);
      setUserContext(userContext);

      //Call aimsAvailability api
      if (["host", "coHost"].includes(userContext.role) && runningContext.context === "inMeeting") {
        LogManager.info("The app is running inMeeting and user is host or coHost.");

        //Only host and coHost can call this api
        const meetingContext = await zoomSdk.getMeetingContext();
        LogManager.info("The meetingContext is: ", meetingContext);
        setMeetingId(meetingContext.meetingID);

        const aimsAvailability = await fetchAimsAvailabilityApi(
          meetingUUID,
          meetingContext.meetingID,
          meetingContext.meetingTopic,
          userContext.screenName
        );
        handleAimsAvailabilityResult(aimsAvailability);
      } else {
        LogManager.info("The app is running inMeeting and user is attendee.");
        return <AttendeeHomeView meetingUUID={meetingUUID} aimsStatus={AIMS_STATUS.AVAILABLE}/>
      }
    } catch (error) {
      LogManager.error("Error fetching context or call AimsAvailability API:", error);
      setAimsStatus(AIMS_STATUS.EXCEPTION);
    } finally {
      setIsLoading(false);
    }
  };

  // Main effect: configure SDK and fetch AIMS status
  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Running in localhost mode.");
      handleAimsAvailabilityResult(AimsAvailabilityApiResponse);
      setIsOrganizer(false);
      //Running context value: inMeeting, inMainClient
      setRunningContext("inMeeting");
      setMeetingUUID("testUUID");
      setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      setIsLoading(false);

      setIsSummarizing(false);
    } else {
      initializeZoomSdk();
    }
  }, []);

  // redirect to /client when inMainClient
  useEffect(() => {
    if (runningContext === "inMainClient") {
      LogManager.info("Detected inMainClient context, redirecting to /client.");
      navigate("/client");
    }
  }, [runningContext, navigate]);

  return (
    <Column spacing="none">
      <AimsMasthead selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      {selectedTab === TABS.SUMMARIES ? (
        <Summaries />
      ) : selectedTab === TABS.HELP ? (
        <Help />
      ) : selectedTab === TABS.MAIN ? (
        isLoading ? (
          <Loading />
        ) : aimsStatus === AIMS_STATUS.EXCEPTION ? (
          <AimsException />
        ) : aimsStatus === AIMS_STATUS.NOT_AVAILABLE ? (
          <AimsNotAvailable />
        ) : isOrganizer ? (
          <SummarizeBox
            isSummarizing={isSummarizing}
            setIsSummarizing={setIsSummarizing}
            meetingId={meetingId}
            meetingUuid={meetingUUID}
            aimsStatus={aimsStatus}
          />
        ) : (
          <AttendeeHomeView meetingUUID={meetingUUID} aimsStatus={AIMS_STATUS.AVAILABLE} />
        )
      ) : null}
    </Column>
  );
};

export default Home;
